import React from 'react';
import { Button, Col, Collapse, Row, Card, Typography } from 'antd';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { useDeepEffect } from 'utils/useDeepEffect';
import { Status } from 'components/form/Status/Status';
import { CatalogAsyncSelect } from 'components/form/selects/CatalogAsyncSelect';
import { FormActions } from 'components/Layout/FormActions/FormActions';
import { isEmpty } from 'utils/helpers';
import { ISalesItem, TSalesDraftItem } from 'store/slices/sales/interfaces';
import { TextAreaField } from 'components/form/base/TextAreaField';
import { InputAutoCompleteField } from 'components/form/base/InputAutoCompleteField';
import { TextAreaAutoCompleteField } from 'components/form/base/TextAreaAutoCompleteField';
import { DatePicker } from 'components/form/base/DatePicker';
import { AddMaterial } from 'components/form/AddMaterial';
import { ExtensionContacts } from 'components/form/ExtensionContacts/ExtensionContacts';
import { ProcedureStatusSelect } from 'components/form/selects/ProcedureStatusSelect';
import { Statuses } from 'constants/status';
import { MaterialValue } from 'components/form/AddMaterial/components/MaterialItem/types';
import {
  TProcedureStatuses,
  TProcedureStatusesNames,
} from 'constants/procedureStatus';

import { mapValues, mapValuesToDraft, validationSchema } from './formUtils';
import {
  DEFAULT_ACCEPT,
  UPLOADS_UNSUPPORTED_FILE_TYPES,
  VALID_FORMATS_LIST,
} from './constants';
import styles from './SalesForm.module.less';

interface IFormValues {
  name: string;
  saleMethodCatalogId: number | { value: number; label: string };
  statusProcedure:
    | TProcedureStatuses
    | { value: TProcedureStatuses; label: TProcedureStatusesNames };
  contactPerson: string;
  timePlaceProcedureDocumentation: string;
  paymentProcurementDocuments: string;
  securingProcurementApplication: string;
  securingContract: string;
  placeOpeningEnvelopes: string;
  dateTimeOpeningEnvelopes: string;
  placeConsiderationApplication: string;
  dateTimeConsiderationApplication: string;
  placeDebriefing: string;
  dateTimeDebriefing: string;
  datePlacementNotice: null;
  noticeNumber: string;
  materials: MaterialValue[];
  initialPrice: string;
  status: Statuses;
}

type TProps = {
  initialValues: IFormValues;
  onFinish: (values: ISalesItem) => void;
  onSaveDraft: (values: TSalesDraftItem) => void;
  cancelButtonHandler: () => void;
  isDraft: boolean;
  isLoading: boolean;
};

export const SalesForm: React.FC<TProps> = ({
  initialValues,
  onFinish,
  onSaveDraft,
  cancelButtonHandler,
  isDraft,
  isLoading,
}) => {
  const [activeCollapseTabs, setActiveCollapseTabs] = React.useState([
    'contacts',
    'status',
  ]);
  const resolver = yupResolver(validationSchema);

  const methods = useForm({
    defaultValues: initialValues,
    resolver,
  });

  const handleSaveAsDraft = () => {
    const formValues = mapValuesToDraft(methods.getValues());
    onSaveDraft(formValues);
  };

  const submit = values => {
    const preparedValues = mapValues(values);
    onFinish(preparedValues);
  };

  useDeepEffect(() => {
    methods.reset(initialValues);
  }, [initialValues]);

  useDeepEffect(() => {
    if (!isEmpty(methods.formState.errors)) {
      setActiveCollapseTabs([
        ...Array.from(
          new Set([
            ...activeCollapseTabs,
            ...Object.keys(methods.formState.errors),
          ])
        ),
      ]);
    }
  }, [methods.formState.errors]);

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(submit)}
        className='ant-form ant-form-vertical indent-top'
      >
        <Card title='Общая информация' bordered={false}>
          <Row>
            <Col span={24}>
              <TextAreaField
                name='name'
                label='Название предмета процедуры продажи'
                required
                placeholder='Введите название'
                maxLength={500}
              />
            </Col>
          </Row>
          <Row gutter={[24, 0]}>
            <Col xs={{ span: 24 }} md={{ span: 12 }} xl={{ span: 10 }}>
              <CatalogAsyncSelect
                name='saleMethodCatalogId'
                label='Способ процедуры продажи'
                placeholder='Выберите способ'
                required
                requestParams={{
                  entityType: 'sales',
                  fieldType: 'saleMethodCatalogId',
                }}
              />
            </Col>
            <Col xs={{ span: 24 }} md={{ span: 12 }} xl={{ span: 10 }}>
              <ProcedureStatusSelect
                name='statusProcedure'
                label='Статус процедуры продажи'
                placeholder='Выберите статус'
                required
              />
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <InputAutoCompleteField
                name='timePlaceProcedureDocumentation'
                params={{
                  entityName: 'sales',
                  field: 'sales.timePlaceProcedureDocumentation',
                }}
                label='Срок, место и порядок предоставления документации об отчуждении'
                maxLength={255}
                required
              />
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <InputAutoCompleteField
                name='paymentProcurementDocuments'
                params={{
                  entityName: 'sales',
                  field: 'sales.paymentProcurementDocuments',
                }}
                label='Внесение платы за документацию об отчуждении'
                maxLength={255}
                required
              />
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <TextAreaAutoCompleteField
                name='securingProcurementApplication'
                label='Обеспечение заявки на участие в процедуре продажи'
                placeholder='Начните печатать текст'
                params={{
                  entityName: 'sales',
                  field: 'sales.securingProcurementApplication',
                }}
                maxLength={500}
                required
              />
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <TextAreaAutoCompleteField
                name='securingContract'
                label='Обеспечение исполнения договора'
                placeholder='Начните печатать текст'
                params={{
                  entityName: 'sales',
                  field: 'sales.securingContract',
                }}
                maxLength={500}
                required
              />
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <InputAutoCompleteField
                name='placeOpeningEnvelopes'
                params={{
                  entityName: 'sales',
                  field: 'sales.placeOpeningEnvelopes',
                }}
                label='Место открытия доступа к заявкам на участие в процедуре продажи'
                maxLength={255}
                required
              />
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <InputAutoCompleteField
                name='dateTimeOpeningEnvelopes'
                params={{
                  entityName: 'sales',
                  field: 'sales.dateTimeOpeningEnvelopes',
                }}
                label='Дата и время открытия доступа к заявкам на участие в процедуре продажи'
                maxLength={255}
                required
              />
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <InputAutoCompleteField
                name='placeConsiderationApplication'
                params={{
                  entityName: 'sales',
                  field: 'sales.placeConsiderationApplication',
                }}
                label='Место рассмотрения заявок на участие в процедуре продажи'
                maxLength={255}
                required
              />
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <InputAutoCompleteField
                name='dateTimeConsiderationApplication'
                params={{
                  entityName: 'sales',
                  field: 'sales.dateTimeConsiderationApplication',
                }}
                label='Дата и время рассмотрения заявок на участие в процедуре продажи'
                maxLength={255}
                required
              />
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <InputAutoCompleteField
                name='placeDebriefing'
                params={{
                  entityName: 'sales',
                  field: 'sales.placeDebriefing',
                }}
                label='Место проведения аукциона'
                maxLength={255}
                required
              />
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <InputAutoCompleteField
                name='dateTimeDebriefing'
                params={{
                  entityName: 'sales',
                  field: 'sales.dateTimeDebriefing',
                }}
                label='Дата и время проведения аукциона'
                maxLength={255}
                required
              />
            </Col>
          </Row>
          <Row className={styles.dateNumberContainer}>
            <Col className={styles.date}>
              <DatePicker
                name='datePlacementNotice'
                label='Дата размещения извещения процедуры продажи'
                placeholder='Выберите дату начала'
                required
              />
            </Col>
            <Col className={styles.number}>
              <InputAutoCompleteField
                name='noticeNumber'
                params={{
                  entityName: 'sales',
                  field: 'sales.noticeNumber',
                }}
                label='Номер извещения процедуры продажи'
                maxLength={60}
                required
              />
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <TextAreaField
                name='initialPrice'
                label='Начальная (минимальная) цена договора'
                maxLength={500}
                required
              />
            </Col>
          </Row>
          <Row>
            <Col span={24} className={styles.materialSection}>
              <Typography.Text>Документы</Typography.Text>
              <AddMaterial
                name='materials'
                title='Загрузить документ'
                className={styles.materialItem}
                validFormatsList={VALID_FORMATS_LIST}
                accept={DEFAULT_ACCEPT}
                uploadsUnsupportedFileType={UPLOADS_UNSUPPORTED_FILE_TYPES}
              />
            </Col>
          </Row>
        </Card>
        <Collapse
          bordered={false}
          expandIconPosition='end'
          onChange={(value: string[]) => setActiveCollapseTabs(value)}
          activeKey={activeCollapseTabs}
        >
          <Collapse.Panel header='Контакты' key='contacts'>
            <ExtensionContacts name='contacts' type='sales' />
          </Collapse.Panel>
          <Collapse.Panel header='Публикация' key='status'>
            <Status status={initialValues.status} isStatusesLimited />
          </Collapse.Panel>
        </Collapse>
        <FormActions>
          <Button onClick={cancelButtonHandler} disabled={isLoading}>
            Отмена
          </Button>
          {isDraft && (
            <Button onClick={handleSaveAsDraft} loading={isLoading}>
              Сохранить как черновик
            </Button>
          )}
          <Button type='primary' htmlType='submit' loading={isLoading}>
            Сохранить
          </Button>
        </FormActions>
      </form>
    </FormProvider>
  );
};
